import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  useToast,
  Input,
  IconButton,
  Flex,
  List,
  ListItem,
  InputGroup,
  InputLeftElement,
  Divider,
  Checkbox,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/react';
import { AddIcon, SearchIcon, QuestionIcon } from '@chakra-ui/icons';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { supabase } from '../supabaseClient';
import CreateGroupModal from './CreateGroupModal';
import HelpModal, { groupsHelpContent } from './HelpModal';

const Groups = () => {
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [members, setMembers] = useState([]);
  const [groupMembers, setGroupMembers] = useState([]);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [isAddingGroup, setIsAddingGroup] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [groupToArchive, setGroupToArchive] = useState(null);
  const toast = useToast();

  useEffect(() => {
    fetchGroups();
    loadMembers();
  }, []);

  const fetchGroups = async () => {
    const { data, error } = await supabase
      .from('member_groups')
      .select('*')
      .eq('is_archived', false)
      .order('name');

    if (error) {
      console.error('Error fetching groups:', error);
      toast({
        title: 'Error fetching groups',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    } else {
      setGroups(data || []);
    }
  };

  const loadMembers = async () => {
    try {
      const { data, error } = await supabase
        .from('texting_contacts')
        .select('*')
        .eq('is_rotary_member', true)
        .eq('consent_status', 'CONSENTED')
        .order('full_name');

      if (error) throw error;
      setMembers(data);
    } catch (error) {
      console.error('Error loading members:', error);
      toast({
        title: 'Error loading members',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  const loadGroupMembers = async (groupId) => {
    try {
      const { data, error } = await supabase
        .from('member_groups_members')
        .select(`
          contact_id,
          texting_contacts (*)
        `)
        .eq('group_id', groupId);

      if (error) throw error;
      setGroupMembers(data.map(d => d.texting_contacts));
    } catch (error) {
      console.error('Error loading group members:', error);
      toast({
        title: 'Error loading group members',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  const addMembersToGroup = async (groupId, memberIds) => {
    try {
      const membersToAdd = memberIds.map(memberId => ({
        group_id: groupId,
        contact_id: memberId
      }));

      const { error } = await supabase
        .from('member_groups_members')
        .insert(membersToAdd);

      if (error) throw error;

      toast({
        title: 'Members added',
        description: 'Members have been added to the group',
        status: 'success',
        duration: 3000,
      });

      // Refresh group members
      loadGroupMembers(groupId);
    } catch (error) {
      console.error('Error adding members to group:', error);
      toast({
        title: 'Error adding members',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  const removeMemberFromGroup = async (groupId, memberId) => {
    try {
      const { error } = await supabase
        .from('member_groups_members')
        .delete()
        .eq('group_id', groupId)
        .eq('contact_id', memberId);

      if (error) throw error;

      toast({
        title: 'Member removed',
        description: 'Member has been removed from the group',
        status: 'success',
        duration: 3000,
      });

      // Refresh group members
      loadGroupMembers(groupId);
    } catch (error) {
      console.error('Error removing member from group:', error);
      toast({
        title: 'Error removing member',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  const checkGroupName = async (name) => {
    const { data, error } = await supabase
      .from('member_groups')
      .select('name, is_archived')
      .ilike('name', name)
      .single();

    if (error && error.code !== 'PGRST116') { // PGRST116 is "no rows returned"
      console.error('Error checking group name:', error);
      return { isUnique: true };
    }

    if (data) {
      return {
        isUnique: false,
        isArchived: data.is_archived
      };
    }

    return { isUnique: true };
  };

  const handleAddGroup = async () => {
    if (!newGroupName.trim()) return;

    const { isUnique, isArchived } = await checkGroupName(newGroupName.trim());
    
    if (!isUnique) {
      toast({
        title: 'Group name already exists',
        description: isArchived 
          ? 'This group name exists but is archived. Please choose a different name.'
          : 'A group with this name already exists. Please choose a different name.',
        status: 'warning',
        duration: 5000,
      });
      return;
    }

    const { error } = await supabase
      .from('member_groups')
      .insert([{ name: newGroupName.trim() }]);

    if (error) {
      console.error('Error adding group:', error);
      toast({
        title: 'Error',
        description: 'Failed to create group',
        status: 'error',
        duration: 5000,
      });
      return;
    }

    setNewGroupName('');
    setIsAddingGroup(false);
    fetchGroups();
  };

  const handleEditGroup = async (group) => {
    if (!editingGroup?.name.trim()) return;
    
    if (editingGroup.name === group.name) {
      setEditingGroup(null);
      return;
    }

    const { data: existingGroup } = await supabase
      .from('member_groups')
      .select('name, is_archived')
      .ilike('name', editingGroup.name.trim())
      .single();

    if (existingGroup) {
      toast({
        title: 'Group name already exists',
        description: existingGroup.is_archived 
          ? 'This group name exists but is archived. Please choose a different name.'
          : 'A group with this name already exists. Please choose a different name.',
        status: 'warning',
        duration: 5000,
      });
      return;
    }

    const { error } = await supabase
      .from('member_groups')
      .update({ name: editingGroup.name.trim() })
      .eq('id', group.id);

    if (error) {
      console.error('Error updating group:', error);
      toast({
        title: 'Error',
        description: 'Failed to update group',
        status: 'error',
        duration: 5000,
      });
      return;
    }

    setEditingGroup(null);
    fetchGroups();
  };

  const handleArchiveGroup = async () => {
    if (!groupToArchive) return;

    const { error } = await supabase
      .from('member_groups')
      .update({ is_archived: true })
      .eq('id', groupToArchive.id);

    if (error) {
      console.error('Error archiving group:', error);
      toast({
        title: 'Error',
        description: 'Failed to archive group',
        status: 'error',
        duration: 5000,
      });
      return;
    }

    setIsArchiveModalOpen(false);
    setGroupToArchive(null);
    if (selectedGroup?.id === groupToArchive.id) {
      setSelectedGroup(null);
    }
    fetchGroups();
    
    toast({
      title: 'Group Archived',
      description: `${groupToArchive.name} has been archived`,
      status: 'success',
      duration: 5000,
    });
  };

  useEffect(() => {
    if (selectedGroup) {
      loadGroupMembers(selectedGroup.id);
    }
  }, [selectedGroup]);

  const handleMemberToggle = async (memberId) => {
    if (!selectedGroup) return;

    const isMember = groupMembers.some(member => member.id === memberId);
    try {
      if (isMember) {
        // Remove member from group
        await removeMemberFromGroup(selectedGroup.id, memberId);
      } else {
        // Add member to group
        await addMembersToGroup(selectedGroup.id, [memberId]);
      }
    } catch (error) {
      console.error('Error updating group membership:', error);
      toast({
        title: 'Error updating group membership',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    }
  };

  const filteredGroups = groups.filter(group =>
    group.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Flex gap={3} p={3}>
      {/* Left Column - Group List */}
      <Box w="300px" bg="gray.50" p={3} borderRadius="md" maxH="calc(100vh - 160px)" display="flex" flexDirection="column">
        <VStack spacing={3} align="stretch">
          <Flex justify="space-between" align="center">
            <Text fontSize="xl" fontWeight="bold">Manage Groups</Text>
            <IconButton
              icon={<AddIcon boxSize={3} />}
              size="xs"
              colorScheme="blue"
              onClick={() => setIsCreateModalOpen(true)}
              aria-label="Create group"
            />
          </Flex>

          <Text fontSize="sm" color="gray.600">
            Select a group to manage its members
          </Text>

          <InputGroup size="md">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Search groups..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </InputGroup>

          <Box overflowY="auto">
            <List spacing={0.5}>
              <ListItem 
                key="all-members"
                p={1.5}
                bg={!selectedGroup ? "blue.100" : "white"}
                borderRadius="md"
                border="1px solid"
                borderColor={!selectedGroup ? "blue.300" : "gray.200"}
                _hover={{ bg: !selectedGroup ? "blue.100" : "gray.50" }}
                cursor="pointer"
                onClick={() => setSelectedGroup(null)}
                mb={0.5}
              >
                <Text fontWeight="bold">All Members</Text>
              </ListItem>
              {filteredGroups
                .filter(group => group.name.toLowerCase() !== 'all members')
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(group => (
                  <ListItem 
                    key={group.id}
                    px={4}
                    py={2}
                    bg={selectedGroup?.id === group.id ? "blue.50" : "white"}
                    _hover={{ bg: selectedGroup?.id === group.id ? "blue.100" : "gray.50" }}
                    cursor="pointer"
                    onClick={() => setSelectedGroup(group)}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    {editingGroup?.id === group.id ? (
                      <Input
                        value={editingGroup.name}
                        onChange={(e) => setEditingGroup({ ...editingGroup, name: e.target.value })}
                        onBlur={() => handleEditGroup(group)}
                        onKeyPress={(e) => e.key === 'Enter' && handleEditGroup(group)}
                        size="sm"
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <>
                        <Text>{group.name}</Text>
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            icon={<BsThreeDotsVertical />}
                            variant="ghost"
                            size="sm"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <MenuList onClick={(e) => e.stopPropagation()}>
                            <MenuItem onClick={() => setEditingGroup(group)}>
                              Edit Name
                            </MenuItem>
                            <MenuItem 
                              onClick={() => {
                                setGroupToArchive(group);
                                setIsArchiveModalOpen(true);
                              }}
                              color="red.500"
                            >
                              Archive
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </>
                    )}
                  </ListItem>
                ))}
            </List>
          </Box>
        </VStack>
      </Box>

      {/* Right Column - Group Details */}
      <Box flex={1} bg="gray.50" p={3} borderRadius="md">
        <VStack spacing={3} align="stretch">
          <Flex justify="space-between" align="center">
            <Text fontSize="xl" fontWeight="bold">
              {selectedGroup ? selectedGroup.name : "All Members"}
            </Text>
            <IconButton
              icon={<QuestionIcon boxSize={4} />}
              size="sm"
              variant="ghost"
              colorScheme="blue"
              onClick={() => setIsHelpModalOpen(true)}
              aria-label="Help"
            />
          </Flex>

          <Text fontSize="sm" color="gray.600">
            {selectedGroup 
              ? "Only members who have given consent to receive messages will be shown"
              : "All members who have given consent to receive messages are automatically included"}
          </Text>

          {selectedGroup ? (
            <>
              <InputGroup size="md">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="Search members..."
                  value={memberSearchQuery}
                  onChange={(e) => setMemberSearchQuery(e.target.value)}
                />
              </InputGroup>

              <Box overflowY="auto" maxH="calc(100vh - 260px)">
                <List spacing={0.5}>
                  {members
                    .filter(member => 
                      `${member.first_name} ${member.last_name}`
                        .toLowerCase()
                        .includes(memberSearchQuery.toLowerCase())
                    )
                    .map(member => (
                      <ListItem 
                        key={member.id}
                        p={1.5}
                        borderRadius="md"
                        border="1px solid"
                        borderColor="gray.200"
                        _hover={{ bg: "gray.50" }}
                      >
                        <Flex justify="space-between" align="center">
                          <Checkbox
                            isChecked={groupMembers.some(m => m.id === member.id)}
                            onChange={() => handleMemberToggle(member.id)}
                          >
                            {member.first_name} {member.last_name}
                          </Checkbox>
                        </Flex>
                      </ListItem>
                    ))
                  }
                </List>
              </Box>
            </>
          ) : (
            <Box overflowY="auto" maxH="calc(100vh - 260px)">
              <List spacing={0.5}>
                {members
                  .filter(member => 
                    `${member.first_name} ${member.last_name}`
                      .toLowerCase()
                      .includes(memberSearchQuery.toLowerCase())
                  )
                  .map(member => (
                    <ListItem 
                      key={member.id}
                      p={1.5}
                      borderRadius="md"
                      border="1px solid"
                      borderColor="gray.200"
                      _hover={{ bg: "gray.50" }}
                    >
                      <Text>{member.first_name} {member.last_name}</Text>
                    </ListItem>
                  ))
                }
              </List>
            </Box>
          )}
        </VStack>
      </Box>

      {/* Add Group Modal */}
      <Modal isOpen={isAddingGroup} onClose={() => setIsAddingGroup(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New Group</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Input
              placeholder="Group name"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddGroup()}
            />
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={handleAddGroup}>
              Add
            </Button>
            <Button onClick={() => setIsAddingGroup(false)}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Archive Confirmation Modal */}
      <Modal isOpen={isArchiveModalOpen} onClose={() => setIsArchiveModalOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Archive Group</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you want to archive "{groupToArchive?.name}"? This group will no longer be visible in the groups list.
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="red" mr={3} onClick={handleArchiveGroup}>
              Archive
            </Button>
            <Button onClick={() => setIsArchiveModalOpen(false)}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <CreateGroupModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onGroupCreated={() => {
          fetchGroups();
          toast({
            title: 'Group created',
            status: 'success',
            duration: 2000,
          });
        }}
      />

      <HelpModal
        isOpen={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
        title="Groups Help"
        content={groupsHelpContent}
      />
    </Flex>
  );
};

export default Groups;
